import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress, Typography } from '@mui/material';
import { LineupProvider } from './contexts/LineupContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AssistantProvider } from './contexts/AssistantContext';
import AssistantWidget from './components/assistant/AssistantWidget';

// Import pages
import Dashboard from './pages/Dashboard';
import Players from './pages/Players';
import Projections from './pages/Projections';
import Lineups from './pages/Lineups';
import LineupsV1 from './pages/LineupsV1';
import TradeAnalysis from './pages/TradeAnalysis';
import LineupPlayground from './pages/LineupPlayground';
import SalaryPlayground from './pages/SalaryPlayground';
import TradeScorecard from './pages/TradeScorecard';
import BattingOrderOptimizer from './pages/BattingOrderOptimizer';
import Login from './pages/Login';
import Navbar from './components/Navbar';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Create theme — Rajasthan Royals branding (rose-pink primary, navy accent)
const theme = createTheme({
  palette: {
    primary: {
      main: '#E74C91',
      light: '#F27BAF',
      dark: '#C13B76',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#1E2A5A',
      light: '#3A4A82',
      dark: '#121B3F',
      contrastText: '#ffffff',
    },
    background: {
      default: '#fbfcfd',
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2430',
      secondary: '#5b6472',
    },
    divider: 'rgba(30,42,90,0.10)',
  },
  // Global radius base — inline sx `borderRadius: N` values scale from this,
  // so raising it smooths curves consistently across every page.
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.5px' },
    h5: { fontWeight: 700, letterSpacing: '-0.3px' },
    h6: { fontWeight: 700 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
  },
  components: {
    // Subtle graph-paper grid behind every page (light so it stays out of the way)
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#fbfcfd',
          backgroundImage:
            'linear-gradient(rgba(30,42,90,0.022) 1px, transparent 1px),' +
            'linear-gradient(90deg, rgba(30,42,90,0.022) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
        },
      },
    },
    // Richer, slightly gradient app bar with a soft shadow
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(95deg, #E7509A 0%, #DE3F87 100%)',
          boxShadow: '0 2px 14px rgba(30,42,90,0.16)',
        },
      },
    },
    // Sentence-case buttons with a soft branded shadow on primary
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 12,
          paddingLeft: 18,
          paddingRight: 18,
        },
        containedPrimary: {
          boxShadow: '0 4px 12px rgba(231,76,145,0.28)',
          '&:hover': { boxShadow: '0 6px 18px rgba(231,76,145,0.38)' },
        },
      },
    },
    // Softer content cards: smoother curves, hairline border, gentle depth
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          border: '1px solid rgba(30,42,90,0.07)',
          boxShadow: '0 2px 12px -2px rgba(30,42,90,0.07)',
        },
      },
    },
    // Papers (tables, panels, dropdown menus) share the same smooth radius
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 16 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 22 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 14 },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 99 },
      },
    },
    // Rounded inputs
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 12 },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    },
    // Modern sentence-case tabs
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, fontSize: '0.95rem' },
      },
    },
  },
});

// Loading Component
const LoadingScreen: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      bgcolor: 'primary.main',
    }}
  >
    <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
    <Typography variant="h6" sx={{ color: 'white' }}>
      Loading...
    </Typography>
  </Box>
);

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// App Routes Component
const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading, login } = useAuth();
  
  // Show loading screen while checking auth
  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <AssistantProvider>
        {/* Bottom padding reserves space for the fixed Ask SquadLab launcher so
            page controls (table pagination etc.) never end up underneath it. */}
        <div className="App" style={{ paddingBottom: isAuthenticated ? 104 : 0 }}>
          {isAuthenticated && <Navbar />}
          <Routes>
          <Route 
            path="/login" 
            element={
              isAuthenticated ? (
                <Navigate to="/lineups" replace />
              ) : (
                <Login onLogin={login} />
              )
            } 
          />
          {/* Dashboard and Players tabs - Deactivated for now */}
          {/* <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/players"
            element={
              <ProtectedRoute>
                <Players />
              </ProtectedRoute>
            }
          /> */}
          
          {/* Redirect root to lineups */}
          <Route path="/" element={<Navigate to="/lineups" replace />} />
          <Route
            path="/projections"
            element={
              <ProtectedRoute>
                <Projections />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lineups"
            element={
              <ProtectedRoute>
                <LineupsV1 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lineups-old"
            element={
              <ProtectedRoute>
                <Lineups />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trade-analysis"
            element={
              <ProtectedRoute>
                <TradeAnalysis />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lineup-playground"
            element={
              <ProtectedRoute>
                <LineupPlayground />
              </ProtectedRoute>
            }
          />
          <Route
            path="/salary-playground"
            element={
              <ProtectedRoute>
                <SalaryPlayground />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trade-scorecard"
            element={
              <ProtectedRoute>
                <TradeScorecard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/batting-order-optimizer"
            element={
              <ProtectedRoute>
                <BattingOrderOptimizer />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          {isAuthenticated && <AssistantWidget />}
        </div>
      </AssistantProvider>
    </Router>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <LineupProvider>
            <AppRoutes />
          </LineupProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
